import { useEffect, useState, useCallback } from "react";
import styles from "../../styles/components/templates/TemplatePreview.module.css";
import Button from "../../ui/buttons/Button.jsx";
import RichTextEditor from "../../ui/inputs/RichTextEditor.jsx";
import BaseInput from "../../ui/inputs/BaseInput/BaseInput.jsx";
import Avatar from "../../ui/avatar/Avatar.jsx";
import AttachmentPickerModal from "./AttachmentPickerModal.jsx";
import { nativeFieldsService } from "../../services/nativeFields.service.js";
import { customFieldsService } from "../../services/customFields.service.js";
import { templatesService } from "../../services/templates.service.js";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner.jsx";
import { ConfirmDeleteModal } from "../admin/AdminDetailModals.jsx";
import ModalOverlay from "../../ui/Modals/ModalOverlay.jsx";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import addIcon from "../../assets/icons/add.svg";
import {
  MAX_ATTACHMENT_SIZE_BYTES,
  formatFileSize,
  getAttachmentDisplayName,
  getAttachmentSizeInBytes,
  validateAttachmentSize,
} from "../../utils/fileUtils.js";

function getAttachmentSize(attachment) {
  const bytes = getAttachmentSizeInBytes(attachment);

  if (bytes !== null) return formatFileSize(bytes);

  return attachment.size || attachment.file_size || attachment.size_display || "";
}

function attachmentBytes(attachment) {
  return getAttachmentSizeInBytes(attachment) ?? 0;
}

function TemplateFilePreview({
  onClose,
  data = null,
  account,
  reCallTemplates,
  templateGroupId,
  defaultOrden = 1,
  onError,
}) {
  const isEditing = Boolean(data?.id);

  const [tags, setTags] = useState([]);
  const [text, setText] = useState(data?.text || "");
  const [asunto, setAsunto] = useState(data?.name || "");
  const [orden, setOrden] = useState(data?.orden || defaultOrden);
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [addingAttachmentCount, setAddingAttachmentCount] = useState(0);
  const [unlinkingAttachmentId, setUnlinkingAttachmentId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAddingAttachment = addingAttachmentCount > 0;

  const displayedAttachmentSizes = isEditing
    ? attachments.map(attachmentBytes)
    : [...pendingAttachments.map(attachmentBytes), ...pendingFiles.map(attachmentBytes)];

  const attachmentsTotalSize = displayedAttachmentSizes.reduce(
    (total, size) => total + size,
    0,
  );

  const canAddAttachmentOfSize = (sizeInBytes) => {
    const sizeErrorMessage = validateAttachmentSize({
      fileSize: sizeInBytes,
      currentTotalSize: attachmentsTotalSize,
    });

    if (sizeErrorMessage) {
      onError?.(sizeErrorMessage);
      return false;
    }

    return true;
  };

  useEffect(() => {
    setText(data?.text || "");
    setAsunto(data?.name || "");
    if (data) setOrden(data.orden || 1);
  }, [data]);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const [nativeFields, customFields] = await Promise.all([
          nativeFieldsService.fetchNativeFields(),
          account?.id
            ? customFieldsService.fetchCustomFields(account.id)
            : Promise.resolve({ results: [] }),
        ]);

        setTags(
          [
            ...(nativeFields.results || []),
            ...(customFields.results || []),
          ].map((field) => field.code),
        );
      } catch (error) {
        console.error("Error fetching template tags:", error);
        onError?.("No se pudieron cargar las etiquetas del editor.");
        setTags([]);
      }
    };

    fetchTags();
  }, [onError, account?.id]);

  const fetchAttachments = useCallback(async () => {
    if (!data?.id) return;

    try {
      const response = await templatesService.fetchAttachmentsForTemplateFile(
        data.id,
      );

      setAttachments(response.results || []);
    } catch (error) {
      console.error("Error fetching template file attachments:", error);
      setAttachments([]);
    }
  }, [data?.id]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const validateForm = () => {
    const errors = [];
    const parsedOrder = Number(orden);

    if (
      Number.isNaN(parsedOrder) ||
      !Number.isInteger(parsedOrder) ||
      parsedOrder <= 0
    ) {
      errors.push("El orden debe ser un número entero positivo mayor a 0.");
    }

    if (!String(asunto || "").trim()) {
      errors.push("El asunto no puede estar vacío.");
    }

    if (!String(text || "").trim()) {
      errors.push("El contenido no puede estar vacío.");
    }

    if (errors.length > 0) {
      onError?.(errors.join("\n"));
      return null;
    }

    return {
      name: asunto,
      text,
      orden: parsedOrder,
    };
  };

  const handleSave = async () => {
    const payload = validateForm();

    if (!payload) return;

    try {
      if (isEditing) {
        await templatesService.updateTemplateFiles(data.id, payload);
      } else {
        const created = await templatesService.createTemplateFile({
          ...payload,
          template_group: templateGroupId,
        });

        if (created?.id) {
          await Promise.all([
            ...pendingFiles.map((file) => handleUploadFile(file, created.id)),
            ...pendingAttachments.map((attachment) =>
              handleLinkAttachment(attachment, created.id),
            ),
          ]);
        }
      }

      reCallTemplates?.();
      onClose?.();
    } catch (error) {
      console.error("Error saving template file:", error);

      onError?.(
        error?.response?.data?.detail ||
          (isEditing
            ? "No se pudo actualizar el Template File."
            : "No se pudo crear el Template File."),
      );
    }
  };

  const handleUploadFile = async (file, targetId) => {
    const id = targetId ?? data?.id;
    if (!file || !id) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("template_file_id", String(id));

    setAddingAttachmentCount((count) => count + 1);

    try {
      await templatesService.createTemplateFileAttachment(formData);
      if (id === data?.id) await fetchAttachments();
    } catch (error) {
      console.error("Error uploading template file attachment:", error);

      const backendNameError = error?.response?.data?.name;
      const nameErrorMessage = Array.isArray(backendNameError)
        ? backendNameError.join(" ")
        : backendNameError;

      onError?.(
        nameErrorMessage ||
          error?.response?.data?.detail ||
          "No se pudo agregar el archivo adjunto.",
      );
    } finally {
      setAddingAttachmentCount((count) => count - 1);
    }
  };

  const handleLinkAttachment = async (attachment, targetId) => {
    const id = targetId ?? data?.id;
    if (!attachment?.id || !id) return;

    setAddingAttachmentCount((count) => count + 1);

    try {
      await templatesService.linkTemplateFileAttachment(attachment.id, id);
      if (id === data?.id) await fetchAttachments();
    } catch (error) {
      console.error("Error linking template file attachment:", error);
      onError?.(
        error?.response?.data?.detail ||
          "No se pudo vincular el archivo adjunto.",
      );
    } finally {
      setAddingAttachmentCount((count) => count - 1);
    }
  };

  const handlePickExistingAttachment = async (attachment) => {
    setShowAttachmentPicker(false);

    if (!canAddAttachmentOfSize(attachmentBytes(attachment))) return;

    if (isEditing) {
      await handleLinkAttachment(attachment);
      return;
    }

    setPendingAttachments((prev) =>
      prev.some((pending) => pending.id === attachment.id)
        ? prev
        : [...prev, attachment],
    );
  };

  const handlePickNewFile = async (file) => {
    setShowAttachmentPicker(false);

    if (!file) return;
    if (!canAddAttachmentOfSize(file.size)) return;

    if (isEditing) {
      await handleUploadFile(file);
    } else {
      setPendingFiles((prev) => [...prev, file]);
    }
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (isAddingAttachment) return;

    const file = event.dataTransfer.files?.[0];

    if (!file) return;
    if (!canAddAttachmentOfSize(file.size)) return;

    if (isEditing) {
      await handleUploadFile(file);
    } else {
      setPendingFiles((prev) => [...prev, file]);
    }
  };

  const handleDeleteTemplateFile = async () => {
    try {
      await templatesService.deleteTemplateFile(data.id);
      setShowDeleteConfirm(false);
      reCallTemplates?.();
      onClose?.();
    } catch (error) {
      console.error("Error deleting template file:", error);
      setShowDeleteConfirm(false);
      onError?.(
        error?.response?.data?.detail ||
          "No se pudo eliminar el Template File.",
      );
    }
  };

  const handleUnlinkAttachment = async (attachmentId) => {
    if (unlinkingAttachmentId) return;

    setUnlinkingAttachmentId(attachmentId);

    try {
      await templatesService.unlinkTemplateFileAttachment(attachmentId, data.id);
      await fetchAttachments();
    } catch (error) {
      console.error("Error unlinking template file attachment:", error);
      onError?.(
        error?.response?.data?.detail ||
          "No se pudo quitar el archivo adjunto.",
      );
    } finally {
      setUnlinkingAttachmentId(null);
    }
  };

  return (
    <ModalOverlay className={styles.ModalOverlay} onClose={onClose}>
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.modalContent}>
          {isEditing && (
            <button
              type="button"
              className={styles.trashBtn}
              aria-label="Eliminar Template File"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <TrashIcon className={styles.trashIcon} />
            </button>
          )}

          <div className={styles.templateTagId}>#{isEditing && data.orden}</div>

          <h2 className={styles.title}>
            {isEditing ? data.name : "Nuevo Template"}
          </h2>

          <div className={styles.AvatarTagGroup}>
            <Avatar
              src={account?.avatar_url ?? account?.photo}
              name={account?.name || "Sin Cuenta"}
            />
          </div>

          <div className={styles.asuntoRow}>
            <span className={styles.label} style={{ width: 120, flexShrink: 0 }}>Asunto</span>
            <div className={styles.asuntoInputWrapper}>
              <BaseInput
                placeholder="Asunto"
                value={asunto}
                onChange={setAsunto}
                inputClassName={styles.asuntoInput}
              />
            </div>
          </div>

          <RichTextEditor
            label="Contenido"
            value={text}
            templateTags={tags}
            onChange={setText}
          />

          {displayedAttachmentSizes.length > 0 && (
            <div className={styles.attachmentsTotal}>
              {`Adjuntos: ${formatFileSize(attachmentsTotalSize)} de ${formatFileSize(
                MAX_ATTACHMENT_SIZE_BYTES,
              )}`}
            </div>
          )}

          <div className={styles.attachmentsList}>
            {isEditing ? (
              attachments.map((attachment) => (
                <div key={attachment.id} className={styles.attachmentCard}>
                  <span className={styles.attachmentName}>
                    {getAttachmentDisplayName(attachment)}
                  </span>

                  {getAttachmentSize(attachment) && (
                    <span className={styles.attachmentSize}>
                      ({getAttachmentSize(attachment)})
                    </span>
                  )}

                  <button
                    type="button"
                    className={styles.removeAttachmentButton}
                    onClick={() => handleUnlinkAttachment(attachment.id)}
                    disabled={unlinkingAttachmentId === attachment.id}
                  >
                    {unlinkingAttachmentId === attachment.id ? (
                      <span data-testid="unlink-attachment-spinner">
                        <LoadingSpinner size={12} />
                      </span>
                    ) : (
                      "×"
                    )}
                  </button>
                </div>
              ))
            ) : (
              <>
                {pendingAttachments.map((attachment) => (
                  <div key={`existing-${attachment.id}`} className={styles.attachmentCard}>
                    <span className={styles.attachmentName}>
                      {getAttachmentDisplayName(attachment)}
                    </span>

                    {getAttachmentSize(attachment) && (
                      <span className={styles.attachmentSize}>
                        ({getAttachmentSize(attachment)})
                      </span>
                    )}

                    <button
                      type="button"
                      className={styles.removeAttachmentButton}
                      onClick={() =>
                        setPendingAttachments((prev) =>
                          prev.filter((pending) => pending.id !== attachment.id),
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}

                {pendingFiles.map((file, index) => (
                  <div key={`pending-${index}`} className={styles.attachmentCard}>
                    <span className={styles.attachmentName}>{file.name}</span>

                    {formatFileSize(file.size) && (
                      <span className={styles.attachmentSize}>
                        ({formatFileSize(file.size)})
                      </span>
                    )}

                    <button
                      type="button"
                      className={styles.removeAttachmentButton}
                      onClick={() =>
                        setPendingFiles((prev) =>
                          prev.filter((_, i) => i !== index),
                        )
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>

          <button
            type="button"
            className={`${styles.fileDropArea} ${
              isDragging ? styles.fileDropAreaDragging : ""
            }`}
            aria-label="Agregar archivo adjunto"
            disabled={isAddingAttachment}
            onClick={() => setShowAttachmentPicker(true)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => {
              setIsDragging(false);
            }}
            onDrop={handleDrop}
          >
            {isAddingAttachment ? (
              <span data-testid="attachment-adding-spinner">
                <LoadingSpinner size={24} />
              </span>
            ) : (
              <img src={addIcon} alt="" className={styles.fileDropIcon} />
            )}
          </button>

          <Button
            variant="primary"
            tone="blue"
            width={172}
            height={48}
            style={{ marginTop: 16, alignSelf: "flex-end" }}
            onClick={handleSave}
          >
            {isEditing ? "Guardar" : "Crear"}
          </Button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div onClick={(event) => event.stopPropagation()}>
          <ConfirmDeleteModal
            title="¿Eliminar este Template File?"
            description="Esta acción no tiene vuelta atrás."
            name={data?.name}
            onConfirm={handleDeleteTemplateFile}
            onClose={() => setShowDeleteConfirm(false)}
          />
        </div>
      )}

      {showAttachmentPicker && (
        <AttachmentPickerModal
          accountId={account?.id}
          linkedAttachmentIds={
            isEditing
              ? attachments.map((attachment) => attachment.id)
              : pendingAttachments.map((attachment) => attachment.id)
          }
          onClose={() => setShowAttachmentPicker(false)}
          onSelectExisting={handlePickExistingAttachment}
          onUploadNew={handlePickNewFile}
          onError={onError}
        />
      )}
    </ModalOverlay>
  );
}

export default TemplateFilePreview;
